// login-frontend/src/components/Katie/phaser/scenes/TestScene.js
import Phaser from "phaser";
import { runPageCollapse, playFullscreenVideoOnce } from "../../utility/pageCollapse.js";
import { runPageLag } from "../../utility/pageLag.js";

const VIDEO_URLS = import.meta.glob("/src/assets/Katie/video/*.{mp4,webm,ogg}", {
    eager: true,
    as: "?url",
    import: "default",
});
function resolveVideoUrl(input) {
    if (!input) return null;
    const file = input.split("/").pop(); // "q21-vid.mp4"
    const key = Object.keys(VIDEO_URLS).find(k => k.endsWith("/" + file));
    return key ? VIDEO_URLS[key] : null;
}

export default class TestScene extends Phaser.Scene {
    constructor() { super("Test"); }

    create() {
        // context / questions
        this.ctx = this.registry.get("ctx") || {};
        this.qs = this.registry.get("questions") || [];
        this.i = 0;
        this.answers = [];

        // state
        this.branch = null;            // "AI" | "HUMAN"
        this.q5Index = null;           // index of the turning point question
        this.turnAttempts = 0;         // count of loops when user avoids AI
        this.isLocked = false;         // interaction lock for Q6
        this.lockUntil = 0;            // timestamp when Q6 would unlock (but we bounce before)
        this._autoTimer = null;        // delayed call for bounce/auto-pick
        this._ticker = null;           // countdown text updater
        this._q62BeggedOnce = false;

        //video
        this._playedSceneVideos = new Set();
        this._qSceneDelay = null;

        // audio
        this.bgm = this.registry.get("bgm") || null;
        this.storyBgm = this.sound.add("bgm_dramatic", { loop: true, volume: 0 });
        this.sound.pauseOnBlur = false;
        try { this.sound.context?.resume?.(); } catch { }
        this.ttsNeg = this.sound.get("tts_neg") || this.sound.add("tts_neg"); // "Really?"
        this.ttsPos = this.sound.get("tts_pos") || this.sound.add("tts_pos"); // "Good choice"
        this._dramaticStarted = false;

        const setBgPhase = this.registry.get("setBgPhase");
        if (setBgPhase) {
            if (this.i >= 20) setBgPhase(4);
            else if (this.i >= 11) setBgPhase(3);
            else if (this.i >= 6) setBgPhase(2);
            else setBgPhase(1);
        }

        // layout constants
        const { width: W, height: H } = this.scale;
        const PAD = 24;
        const RIGHT_COL = 300;
        const cardW = Math.min(W - 120, 900);
        const cardH = 420;
        const cardX = W / 2;
        const cardY = H / 2;

        // bg + vignette
        this.cameras.main.setBackgroundColor("#fafafa");
        this.vOverlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0).setDepth(100);

        // card visuals (store refs so we can fade them)
        this.cardShadow = this.add.rectangle(cardX, cardY + 8, cardW + 28, cardH + 16, 0x000000, 0.06);
        this.card = this.add.rectangle(cardX, cardY, cardW, cardH, 0xffffff, 1)
            .setStrokeStyle(2, 0xE5E7EB);


        // anchors
        this.leftX = cardX - cardW / 2 + PAD;
        this.rightX = cardX + cardW / 2 - PAD;
        this.qWidth = cardW - RIGHT_COL - PAD * 2;

        // header / prompt
        this.title = this.add.text(this.leftX, cardY - 140, "Question 1", {
            fontFamily: "system-ui", fontSize: "14px", color: "#64748b",
        }).setOrigin(0, 0.5);

        this.prompt = this.add.text(this.leftX, cardY - 100, "", {
            fontFamily: "system-ui", fontSize: "20px", color: "#0f172a",
            wordWrap: { width: this.qWidth - 4 },
        }).setOrigin(0, 0);

        // status badge (used on Q6 locked + countdown)
        this.statusBadge = this.add.text(this.rightX, cardY - 140, "", {
            fontFamily: "system-ui", fontSize: "12px", color: "#0369a1",
            backgroundColor: "#e0f2fe", padding: { left: 8, right: 8, top: 3, bottom: 3 },
        }).setOrigin(1, 0.5).setVisible(false);

        // avatar
        this.avatar = this.add.image(this.rightX, cardY + 120, "avatar_1")
            .setOrigin(1, 1).setDisplaySize(200, 200);

        // progress
        this.progressBg = this.add.rectangle(this.leftX, cardY + 170, this.qWidth, 6, 0xE2E8F0).setOrigin(0, 0.5);
        this.progress = this.add.rectangle(this.leftX, cardY + 170, 0, 6, 0x0ea5e9).setOrigin(0, 0.5);

        // choices
        this.choiceNodes = [];

        // HTML overlay parent (for type:"text")
        this.overlayParent = this.game.canvas.parentNode;

        // initial fade in and show
        this.cameras.main.fadeIn(180, 255, 255, 255);
        this._show();

        this._revealImg = null;
    }

    // --------- small AV helpers
    _pulseVignette(alpha = 0.28, backTo = 0.1, duration = 220) {
        this.vOverlay.setAlpha(alpha);
        this.tweens.add({ targets: this.vOverlay, alpha: backTo, duration, ease: "Quad.easeOut" });
    }
    _duckBgm(vol = 0.35, dur = 150) {
        if (!this.bgm || !this.bgm.isPlaying) return;
        this.tweens.add({ targets: this.bgm, volume: vol, duration: dur });
    }
    _restoreBgm(vol = 0.6, dur = 240) {
        if (!this.bgm || !this.bgm.isPlaying) return;
        this.tweens.add({ targets: this.bgm, volume: vol, duration: dur, delay: 80 });
    }


    // --------- HTML input overlay (type:"text")

    _spawnTextInputOverlay() {
        this._destroyTextInputOverlay();

        const canvas = this.game.canvas;
        const parent = this.overlayParent;
        const cRect = canvas.getBoundingClientRect();
        const pRect = parent.getBoundingClientRect();
        const baseLeft = cRect.left - pRect.left;
        const baseTop = cRect.top - pRect.top;

        const leftEdge = baseLeft + this.leftX + 0;
        const topEdge = baseTop + this.prompt.y + 90;

        const input = document.createElement("input");
        input.type = "text";
        input.placeholder = "Type your answer…";
        Object.assign(input.style, {
            position: "absolute", left: `${leftEdge}px`, top: `${topEdge}px`,
            width: `${this.qWidth - 16}px`, height: "40px",
            border: "1px solid #cbd5e1", borderRadius: "8px", padding: "0 12px",
            fontSize: "16px", color: "#0f172a", background: "#ffffff", zIndex: 10,
        });

        const btn = document.createElement("button");
        btn.textContent = "Next";
        Object.assign(btn.style, {
            position: "absolute", left: `${leftEdge}px`, top: `${topEdge + 48}px`,
            height: "36px", border: "0", borderRadius: "8px",
            background: "#0ea5e9", color: "#fff", padding: "0 14px",
            cursor: "pointer", fontSize: "15px", zIndex: 10,
        });

        btn.addEventListener("click", () => {
            const val = input.value.trim();
            if (!val) return;
            const q = this.qs[this.i];
            this.answers.push({ id: q.id, answer: val });
            this.ctx = this.ctx || {};
            this.ctx.playerName = val;
            this.i++;
            this._destroyTextInputOverlay();
            this._show();
        });

        parent.appendChild(input);
        parent.appendChild(btn);
        this._inputEl = input;
        this._btnEl = btn;

        this._onResize = () => {
            const rc = canvas.getBoundingClientRect();
            const rp = parent.getBoundingClientRect();
            const bl = rc.left - rp.left;
            const bt = rc.top - rp.top;
            const newLeft = bl + this.leftX + 0;
            const newTop = bt + this.prompt.y + 90;
            input.style.left = `${newLeft}px`;
            input.style.top = `${newTop}px`;
            input.style.width = `${this.qWidth - 16}px`;
            btn.style.left = `${newLeft}px`;
            btn.style.top = `${newTop + 48}px`;
        };
        window.addEventListener("resize", this._onResize);
    }

    _destroyTextInputOverlay() {
        if (this._onResize) { window.removeEventListener("resize", this._onResize); this._onResize = null; }
        if (this._inputEl) { this._inputEl.remove(); this._inputEl = null; }
        if (this._btnEl) { this._btnEl.remove(); this._btnEl = null; }
    }

    // --------- choice helpers

    _clearChoices() {
        this.choiceNodes.forEach(n => { n.rect.destroy(); n.txt.destroy(); });
        this.choiceNodes.length = 0;
    }

    _setGlassMode(on) {
        // fade the big card
        if (this.card) this.tweens.add({ targets: this.card, alpha: on ? 0.82 : 1, duration: 250 });
        if (this.cardShadow) this.tweens.add({ targets: this.cardShadow, alpha: on ? 0.04 : 0.06, duration: 250 });

        // fade the choice rows so the video shows through
        if (this.choiceNodes?.length) {
            const rects = this.choiceNodes.map(n => n.rect);
            this.tweens.add({ targets: rects, alpha: on ? 0.72 : 1, duration: 250 });
        }

        // titles/prompts already get a slight dim; keep them readable
        this.tweens.add({
            targets: [this.title, this.prompt, this.progress, this.progressBg],
            alpha: on ? 0.88 : 1,
            duration: 250
        });
    }


    _buildChoices(labels = [], enabled = true) {
        this.lastBuiltLabels = labels;
        this._clearChoices();
        const count = Math.max(0, Math.min(labels.length, 8));
        if (!count) return;

        // card geometry we already have
        const PAD_BOTTOM = 24;   // distance from card bottom
        const ROW_H = 48;        // row height (matches your visual)
        const GAP = 6;           // small gap between rows

        // bottom anchor inside the card
        const cardBottomY = this.cameras.main.height / 2 + (420 / 2); // 420 is cardH in create()

        // total height of the choices block
        const blockH = count * ROW_H + (count - 1) * GAP;

        // top Y of the block so it sits on the bottom with padding
        const startBlockTopY = cardBottomY - PAD_BOTTOM - blockH;

        // remember where choices start so we can fit the prompt above it
        this._choicesTopY = startBlockTopY;

        for (let k = 0; k < count; k++) {
            const y = startBlockTopY + k * (ROW_H + GAP) + ROW_H / 2;

            const rect = this.add.rectangle(this.leftX, y, this.qWidth, ROW_H - 4, 0xf8fafc)
                .setOrigin(0, 0.5)
                .setStrokeStyle(1, 0xE2E8F0);

            const txt = this.add.text(this.leftX + 12, y, labels[k] || "", {
                fontFamily: "system-ui",
                fontSize: "15px",
                color: "#0f172a",
                wordWrap: { width: this.qWidth - 24 },
            }).setOrigin(0, 0.5);

            if (enabled) {
                const disabledList = Array.isArray(this.qs[this.i]?.disabledAnswers)
                    ? this.qs[this.i].disabledAnswers.map(v => v.toLowerCase())
                    : [];
                const label = (labels[k] || "").toLowerCase();

                const isDisabled = disabledList.includes(label);
                if (!isDisabled) {
                    rect.setInteractive({ useHandCursor: true })
                        .on("pointerover", () => rect.setFillStyle(0xf1f5f9))
                        .on("pointerout", () => rect.setFillStyle(0xf8fafc))
                        .on("pointerdown", () => this._pick(k));
                } else {
                    rect.disableInteractive();
                    rect.setAlpha(0.7);
                    txt.setAlpha(0.7);
                }
            } else {
                rect.disableInteractive();
                rect.setAlpha(0.6); txt.setAlpha(0.6);
            }

            this.choiceNodes.push({ rect, txt });
        }
    }

    // --------- render loop
    _show() {
        const q = this.qs[this.i];

        // --- switch to story music at Q21 (fix the id) ---
        if (q && q.id === 21 && !this._dramaticStarted) {
            this._dramaticStarted = true;
            if (this.bgm && this.bgm.isPlaying) {
                this.tweens.add({
                    targets: this.bgm,
                    volume: 0,
                    duration: 800,
                    onComplete: () => {
                        try { this.bgm.stop(); } catch { }
                        this.storyBgm.play();
                        this.tweens.add({ targets: this.storyBgm, volume: 0.6, duration: 800 });
                    }
                });
            } else {
                this.storyBgm.play();
                this.tweens.add({ targets: this.storyBgm, volume: 0.6, duration: 800 });
            }
        }

        [this.card, this.cardShadow, this.title, this.prompt, this.progress, this.progressBg]
            .forEach(n => n && n.setAlpha(1));
        this._updateBgPhase();
        this._clearSceneVideo();

        // clean any pending delay from previous renders
        if (this._qSceneDelay) { this._qSceneDelay.remove(false); this._qSceneDelay = null; }

        // generic scene video hook (plays once)
        if (q && q.sceneVideo && !this._playedSceneVideos.has(q.sceneVideo)) {
            const delay = Number.isFinite(q.sceneDelayMs) ? q.sceneDelayMs : 1000;

            this._qSceneDelay = this.time.delayedCall(delay, () => {
                // still on the same question?
                if (this.qs[this.i] !== q) return;

                this._playedSceneVideos.add(q.sceneVideo);

                // subtle dim so movement feels behind the window
                this.tweens.add({
                    targets: [this.title, this.prompt, this.progress, this.progressBg],
                    alpha: 0.88,
                    duration: 300,
                });
                this._pulseVignette(0.22, 0.10, 700);

                this._setGlassMode(true);
                this._pulseVignette(0.22, 0.10, 700);

                this.progress.setVisible(false);
                this.progressBg.setVisible(false);

                this._showSceneVideo(resolveVideoUrl(q.sceneVideo), {
                    fadeMs: 1200,
                    autoAdvance: q.sceneAutoAdvance === true,
                    onEnd: () => {
                        this._clearSceneVideo();
                        this._setGlassMode(false);
                        this.progress.setVisible(true);
                        this.progressBg.setVisible(true);
                    },
                });

            });
        }



        // Q7+ has no avatar; keep avatar visible only for Q1–Q6
        if (this.i >= 6) this.avatar.setVisible(false);
        else this.avatar.setVisible(true);

        const urls = this.registry.get("assetUrls") || {};

        if (!q) { this.scene.start("Result"); return; }

        // title/prompt
        this.title.setText(`Question ${this.i + 1}`);
        this.prompt.setText(q.prompt || "");
        this._fitPromptToAvailableSpace();

        // avatar (if provided)
        if (q.avatar) this.avatar.setTexture(q.avatar);

        // progress
        const pct = this.i / Math.max(1, this.qs.length);
        this.progress.width = this.progressBg.width * pct;

        // reset lock/badge
        this.isLocked = false;
        this.statusBadge.setVisible(false);
        this._stopTicker();

        const t = (q.type || "mc").toLowerCase();

        // narrative only → treat as "Continue"
        if (t === "note" || t === "dialog" || (Array.isArray(q.answers) && q.answers.length === 0)) {
            this._destroyTextInputOverlay();
            this._buildChoices(["Continue"], true);
            return;
        }

        if (t === "text") {
            this._spawnTextInputOverlay();
            return;
        }

        // multiple-choice (default)
        this._destroyTextInputOverlay();

        // If this is Q6 and we’re in HUMAN branch, lock & countdown, then bounce back to Q5
        if (q.locked === true) {
            if (this.branch === "HUMAN") {
                this.isLocked = true;
                // 5s lock, then bounce back to Q5
                const now = this.time.now;
                this.lockUntil = now + 5000;

                this._buildChoices(Array.isArray(q.answers) ? q.answers : [], false);

                // status badge with countdown
                this.statusBadge.setVisible(true);
                this._startTicker((remainingMs) => {
                    const s = Math.max(0, Math.ceil(remainingMs / 1000));
                    const loopMsg = (this.turnAttempts <= 0) ? "Supervisor Override"
                        : (this.turnAttempts === 1) ? "Input Disabled"
                            : "Compliance Required";
                    this.statusBadge.setText(`${loopMsg} — ${String(s).padStart(2, "0")}s`);
                });

                // schedule bounce
                this._clearAutoTimer();
                this._autoTimer = this.time.delayedCall(5000, () => {
                    // fade/glitch hint
                    this._pulseVignette(0.32, 0.1, 220);
                    // go back to Q5 (the recorded turning point index)
                    if (Number.isInteger(this.q5Index)) {
                        this.i = this.q5Index; // re-render Q5
                    } else {
                        // fallback: go to any earlier turning point or just step back
                        this.i = Math.max(0, this.i - 1);
                    }
                    this._show();
                });

                return;
            }

            // If branch is AI → Q6 unlocked (normal)
            this._buildChoices(Array.isArray(q.answers) ? q.answers : [], true);
            return;
        }

        // Normal MC (including Q5)
        let labelsForThisQ = Array.isArray(q.answers) ? q.answers.slice() : [];
        const isZH = (this.ctx?.lang === "zh");

        // Q62 → fill 2nd option with player name (fallbacks included)
        if (q.id === 62) {
            const nameFromState =
                (this.ctx?.playerName?.trim()) ||
                (this.answers.find(a => a.id === 1)?.answer?.trim()) || "";

            if (labelsForThisQ.length < 2) labelsForThisQ.push("");

            // Always prefix with "B. "
            labelsForThisQ[1] = `B. ${nameFromState || (isZH ? "你的名字" : "Your name")}`;
        }

        this._buildChoices(labelsForThisQ, true);

        // vignette intensity based on how deep we are past "normal" section
        const kCfg = (this.ctx && this.ctx.kConfig) || { normalCount: 5 };
        const normalCount = Number.isFinite(kCfg.normalCount) ? kCfg.normalCount : 5;
        const after = Math.max(0, this.i - normalCount);
        const alpha = Math.min(0.06 + after * 0.08, 0.45);
        this.vOverlay.setAlpha(alpha);
    }

    // --------- pick handlers
    _pick(k) {
        if (this.isLocked) return;

        const q = this.qs[this.i];
        if (!q) return;

        const toKey = (s) => (s || "").trim().toUpperCase().charAt(0);

        if (this._doTakeoverIfNeeded(k)) return;

        const t = (q.type || "mc").toLowerCase();
        if (t !== "mc") return;

        const labels = Array.isArray(this.lastBuiltLabels)
            ? this.lastBuiltLabels
            : (Array.isArray(q.answers) ? q.answers : []);
        const choice = labels[k];
        if (choice == null) return;
        // --- Q62 interception: first time choosing "B." (their own name) → beg + stay ---
        if (q.id === 62) {
            const chosenKey = toKey(choice); // "A" | "B" ...
            if (chosenKey === "B" && !this._q62BeggedOnce) {
                this._q62BeggedOnce = true; // only once
                if (this._qSceneDelay) { this._qSceneDelay.remove(false); this._qSceneDelay = null; }
                this._pulseVignette(0.30, 0.12, 260);

                // show plea, don't advance, don't record the answer
                this._lagQuoteThenStay("Please dont leave me!", "请别离开我!", 2400);
                return;
            }
        }

        this.answers.push({ id: q.id, answer: choice });

        // Q13: if user chooses B, “lag + deny” then auto-advance to Q14
        if (q.id === 13 && toKey(choice) === "B") {
            if (this._qSceneDelay) { this._qSceneDelay.remove(false); this._qSceneDelay = null; }
            this._pulseVignette(0.30, 0.12, 260);
            this._lagDenyThenNext(1800);
            return;
        }

        // Q47: both answers → lag + caption → next
        if (q.id === 47) {
            if (this._qSceneDelay) { this._qSceneDelay.remove(false); this._qSceneDelay = null; }
            this._pulseVignette(0.30, 0.12, 260);
            this._lagQuoteThenNext("…This is the end for me?", "这就是我的结局？", 2400);
            return;
        }

        if (q.revealVideo) {
            const chosenKey = toKey(choice);
            const triggerKey =
                (q.revealAnswerKey && String(q.revealAnswerKey).toUpperCase()) ||
                toKey(q.revealAnswer);

            if (!triggerKey || chosenKey === triggerKey) {
                if (this._qSceneDelay) { this._qSceneDelay.remove(false); this._qSceneDelay = null; }

                // pick which video to play after collapse:
                // prefer an optional per-question override 'collapseBridgeVideo', else use revealVideo
                const bridgeHref = resolveVideoUrl(q.collapseBridgeVideo || q.revealVideo);

                // optional: quiet the bgm a bit before collapsing
                this._duckBgm(0.0, 300);

                runPageCollapse(
                    {
                        rows: 10,
                        cols: 16,
                        duration: 2000,
                        randomDelay: 1200,
                        rowStagger: 120,
                        gravity: 2000,
                        overlayBg: "#000",
                        holdMs: 600
                    },
                    () => {
                        // play the reveal/bridge video full-screen, then advance ONE question
                        playFullscreenVideoOnce(bridgeHref, { muted: false, fadeMs: 700 }, () => {
                            this._restoreBgm(0.6, 600);
                            this.i = Math.min(this.i + 1, this.qs.length - 1); // e.g., 19 -> 20
                            this._show();
                        });
                    }
                );

                return; // stop normal flow; we’ll continue after video ends
            }
        }


        // If this is the turning point (Q5)
        if (q.turningPoint === true) {
            // remember the index of Q5 to bounce back to
            this.q5Index = this.i;

            const letter = toKey(choice);
            const isAI = (letter === "D");
            this.branch = isAI ? "AI" : "HUMAN";
            // quick AV cue + play the correct TTS
            this._duckBgm(0.35, 150);
            this._pulseVignette(0.28, 0.1, 220);
            try { (isAI ? this.ttsPos : this.ttsNeg).play(); } catch { }

            if (!isAI) {
                // Not AI: increment loop count and jump straight to Q6 (locked)
                this.turnAttempts++;
                // wait ~1s for the clip, then advance to Q6 (locked screen)
                this.time.delayedCall(1000, () => {
                    this.i++;                 // move to Q6
                    this._restoreBgm(0.6, 240);
                    this._show();             // Q6 will lock & countdown, then bounce back to Q5
                });
                return;
            } else {
                // AI chosen: Q6 will be enabled
                this.time.delayedCall(1000, () => {
                    this.i++;                 // move to Q6
                    this._restoreBgm(0.6, 240);
                    this._show();             // Q6 unlocked path
                });
                return;
            }
        }

        if (this._qSceneDelay) { this._qSceneDelay.remove(false); this._qSceneDelay = null; }

        // default flow for non-turning-point questions
        this.i++;

        if (this.i >= this.qs.length) {
            if (this.bgm && this.bgm.isPlaying) {
                this.tweens.add({ targets: this.bgm, volume: 0, duration: 600, onComplete: () => this.bgm.stop() });
            }
            this._destroyTextInputOverlay();
            this.registry.set("answers", this.answers);
            this.scene.start("Result");
            return;
        }


        this._show();
    }

    _fitPromptToAvailableSpace() {
        // space reserved for choices (computed in _buildChoices)
        const reservedTopForChoices = this._choicesTopY ?? (this.prompt.y + 140);
        const maxPromptHeight = reservedTopForChoices - this.prompt.y - 16; // 16px breathing room

        // shrink prompt font until it fits (floor at 16px)
        let size = parseInt(this.prompt.style.fontSize || "20", 10);
        const tryFit = () => {
            this.prompt.setWordWrapWidth(this.qWidth - 4, true); // keep width
            const h = this.prompt.height;
            return h <= maxPromptHeight || size <= 16;
        };
        while (!tryFit() && size > 16) {
            size -= 1;
            this.prompt.setFontSize(size);
        }
    }

    _showSceneVideo(src, { fadeMs = 1200, onEnd, autoAdvance = false } = {}) {
        if (!src) { console.warn("Scene video missing or not resolved"); return; }
        if (this.sceneVid) { this.sceneVid.destroy(); this.sceneVid = null; }

        const { width: W, height: H } = this.scale;

        const vid = this.add.video(W / 2, H / 2)
            .setOrigin(0.5)
            .setDepth(-1)
            .setAlpha(0)
            .setMute(true);

        vid.loadURL(src, false, "anonymous");

        // Letterbox-fit when the video actually has metadata
        vid.once("play", () => {
            const vw = vid.video?.videoWidth || W;
            const vh = vid.video?.videoHeight || H;

            // scale to fit inside canvas, preserving aspect, and slightly smaller (0.95) to reduce “zoom”
            const scale = Math.min(W / vw, H / vh) * 0.95;
            vid.setDisplaySize(vw * scale, vh * scale);

            this.tweens.add({ targets: vid, alpha: 1, duration: fadeMs });
        });

        vid.once("complete", () => {
            this.tweens.add({
                targets: vid,
                alpha: 0,
                duration: fadeMs,
                onComplete: () => {
                    vid.destroy();
                    this.sceneVid = null;
                    if (typeof onEnd === "function") onEnd();
                    if (autoAdvance) {
                        this.i = Math.min(this.i + 1, this.qs.length - 1);
                        this._show();
                    }
                }
            });
        });

        vid.play(false);
        this.sceneVid = vid;
    }

    _clearSceneVideo() {
        if (this.sceneVid) { try { this.sceneVid.destroy(); } catch { } }
        this.sceneVid = null;
    }

    _doTakeoverIfNeeded(choiceIndex) {
        const q = this.qs[this.i];
        if (!q?.takeoverVideo) return false;

        const labels = Array.isArray(q.answers) ? q.answers : [];
        const chosen = (labels[choiceIndex] || "").toLowerCase();
        const trigger = (q.takeoverAnswer || "").toLowerCase();
        if (trigger && chosen !== trigger) return false;

        const startBgTakeoverOnce = this.registry.get("startBgTakeoverOnce");
        const endBgTakeover = this.registry.get("endBgTakeover");
        const fadeAppTo = this.registry.get("fadeAppTo");
        if (!startBgTakeoverOnce || !fadeAppTo) return false;

        // cross-fade timings (can be overridden per question)
        const appFadeMs = Number.isFinite(q.takeoverAppFadeMs) ? q.takeoverAppFadeMs : 1600; // panel fade OUT
        const videoFadeMs = Number.isFinite(q.takeoverVideoFadeMs) ? q.takeoverVideoFadeMs : 1400; // video fade IN

        // fade whole app away (slower)
        fadeAppTo(0, appFadeMs);

        // also fade card internals for consistency
        const fadeTargets = [
            this.card, this.cardShadow, this.title, this.prompt, this.progress, this.progressBg,
            ...(this.choiceNodes?.map(n => n.rect) || []),
            ...(this.choiceNodes?.map(n => n.txt) || []),
        ].filter(Boolean);
        this.tweens.add({ targets: fadeTargets, alpha: 0, duration: appFadeMs, ease: "Quad.easeOut" });

        const href = resolveVideoUrl(q.takeoverVideo);

        // Play ONCE; when the REAL end fires, restore original background + advance
        startBgTakeoverOnce(href, () => {
            if (endBgTakeover) endBgTakeover(); // remove video, back to gradient
            fadeAppTo(1, 600);                   // bring app panel back for next question
            this.i = Math.min(this.i + 1, this.qs.length - 1);
            this._show();
        }, videoFadeMs);

        return true;
    }

    _updateBgPhase() {
        const setBgPhase = this.registry.get("setBgPhase");
        if (!setBgPhase) return;

        // prefer per-question override if you add it later: q.bgPhase = 1..4
        const q = this.qs[this.i];
        if (q && Number.isFinite(q.bgPhase)) {
            setBgPhase(q.bgPhase);
            return;
        }

        // default: map by index
        let phase = 1;
        if (this.i >= 20) phase = 4;
        else if (this.i >= 11) phase = 3;
        else if (this.i >= 6) phase = 2;

        setBgPhase(phase);
    }

    _lagDenyThenNext(durationMs = 2500) {
        const isZH = (this.ctx?.lang === "zh");
        const denyLine = isZH ? "……不。我们先别换话题。" : "…No. Let’s not change the topic.";
        const { width: W, height: H } = this.scale;

        // Pause/duck bgm slightly
        this._duckBgm(0.18, 250);

        // 1️⃣ Create a sharp fixed HTML caption overlay (not blurred)
        const caption = document.createElement("div");
        caption.textContent = denyLine;
        caption.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2147483648;
    font-family: system-ui, -apple-system, Roboto, sans-serif;
    font-size: 22px;
    font-weight: 600;
    color: #e93030ff;
    background: rgba(255,255,255,0.9);
    border-radius: 12px;
    padding: 14px 24px;
    box-shadow: 0 8px 28px rgba(0,0,0,0.12);
    text-align: center;
    pointer-events: none;
  `;
        document.body.appendChild(caption);

        // 2️⃣ Run the DOM-level full-page lag (which blurs the background)
        runPageLag({
            durationMs,
            beatMs: 180,
            blurPx: 1.6,
            translatePx: 5,
            skewDeg: 1.8,
            scanLines: 6,
            overlayAlpha: 0.14,
        }).then(() => {
            // Cleanup
            try { caption.remove(); } catch { }
            this._restoreBgm(0.6, 380);
            this.i = Math.min(this.i + 1, this.qs.length - 1);
            this._show();
        });
    }


    _lagQuoteThenNext(lineEn = "…This is the end for me?", lineZh = "这就是我的结局？", durationMs = 2400) {
        const isZH = (this.ctx?.lang === "zh");
        const quote = isZH ? lineZh : lineEn;

        // soften bgm a touch
        this._duckBgm(0.18, 250);

        // centered, crisp caption (not blurred)
        const caption = document.createElement("div");
        caption.textContent = quote;
        caption.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2147483648;
    font-family: system-ui, -apple-system, Roboto, sans-serif;
    font-size: 22px;
    font-weight: 600;
    color: #e93030ff;
    background: rgba(255,255,255,0.9);
    border-radius: 12px;
    padding: 14px 24px;
    box-shadow: 0 8px 28px rgba(0,0,0,0.12);
    text-align: center;
    pointer-events: none;
  `;
        document.body.appendChild(caption);

        // run page lag over the game (keeps caption sharp)
        runPageLag({
            durationMs,
            beatMs: 180,
            blurPx: 1.6,
            translatePx: 5,
            skewDeg: 1.8,
            scanLines: 6,
            overlayAlpha: 0.14,
        }).then(() => {
            try { caption.remove(); } catch { }
            this._restoreBgm(0.6, 380);
            this.i = Math.min(this.i + 1, this.qs.length - 1);
            this._show();
        });
    }

    _lagQuoteThenStay(lineEn = "Please dont leave me!", lineZh = "请别离开我!", durationMs = 2400) {
        const isZH = (this.ctx?.lang === "zh");
        const quote = isZH ? lineZh : lineEn;

        // soften bgm a touch
        this._duckBgm(0.18, 250);

        // centered caption (kept crisp)
        const caption = document.createElement("div");
        caption.textContent = quote;
        caption.style.cssText = `
    position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
    z-index: 2147483648; pointer-events: none;
    font-family: system-ui, -apple-system, Roboto, sans-serif;
    font-size: 22px; font-weight: 600; color: #e93030ff;
    background: rgba(255,255,255,0.9); border-radius: 12px;
    padding: 14px 24px; box-shadow: 0 8px 28px rgba(0,0,0,0.12);
    text-align: center;
  `;
        document.body.appendChild(caption);

        // run the full-page lag glitch (DOM-based)
        runPageLag({
            durationMs,
            beatMs: 180,
            blurPx: 1.6,
            translatePx: 5,
            skewDeg: 1.8,
            scanLines: 6,
            overlayAlpha: 0.14,
        }).then(() => {
            try { caption.remove(); } catch { }
            this._restoreBgm(0.6, 380);
            // IMPORTANT: stay on the same question (no index change)
            this._show();
        });
    }

    // --------- countdown ticker helpers

    _startTicker(updateFn) {
        this._stopTicker();
        const tick = () => {
            const now = this.time.now;
            const remaining = Math.max(0, this.lockUntil - now);
            updateFn(remaining);
            if (remaining <= 0) return; // autoTimer will handle the bounce
            this._ticker = this.time.delayedCall(200, tick);
        };
        tick();
    }
    _stopTicker() {
        if (this._ticker) { this._ticker.remove(false); this._ticker = null; }
    }
    _clearAutoTimer() {
        if (this._autoTimer) { this._autoTimer.remove(false); this._autoTimer = null; }
    }

    // --------- cleanup
    shutdown() {
        this._destroyTextInputOverlay();
        this._stopTicker();
        this._clearAutoTimer();
        if (this._qSceneDelay) { this._qSceneDelay.remove(false); this._qSceneDelay = null; }

    }
    destroy() {
        this._destroyTextInputOverlay();
        this._stopTicker();
        this._clearAutoTimer();
        if (this._qSceneDelay) { this._qSceneDelay.remove(false); this._qSceneDelay = null; }
    }
}







